import { addDays, shortDate } from './format';

/**
 * Date-range presets and labelling.
 *
 * A range is always an inclusive `[from, to]` pair of `YYYY-MM-DD` strings, so a
 * single day is just `from === to`. All arithmetic is done on the ISO strings
 * via UTC to avoid any timezone drift.
 */
export interface DateRange {
  from: string;
  to: string;
}

export type PresetKey =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'last6m'
  | 'last12m'
  | 'all'
  | 'custom';

/** Every preset except `custom`, which has no automatic resolution. */
export type ResolvablePreset = Exclude<PresetKey, 'custom'>;

export interface PresetDef {
  key: ResolvablePreset;
  label: string;
  /** Short form for the trigger button. */
  shortLabel: string;
  hint?: string;
}

export const PRESETS: readonly PresetDef[] = [
  { key: 'today', label: 'Today', shortLabel: 'Today' },
  { key: 'yesterday', label: 'Yesterday', shortLabel: 'Yesterday' },
  { key: 'last7', label: 'Last 7 days', shortLabel: '7 days' },
  { key: 'last30', label: 'Last 30 days', shortLabel: '30 days' },
  { key: 'last6m', label: 'Last 6 months', shortLabel: '6 months' },
  { key: 'last12m', label: 'Last 12 months', shortLabel: '12 months' },
  { key: 'all', label: 'All time', shortLabel: 'All time' },
];

/** Subtract whole months, clamping to the end of a shorter target month. */
export function subMonths(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 - months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

export interface ResolveContext {
  today: string;
  /** Earliest date the warehouse has data for; anchors "All time". */
  earliest: string | null;
}

/** Turn a preset into a concrete range. */
export function resolvePreset(key: ResolvablePreset, ctx: ResolveContext): DateRange {
  const { today, earliest } = ctx;
  switch (key) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { from: y, to: y };
    }
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'last6m':
      return { from: addDays(subMonths(today, 6), 1), to: today };
    case 'last12m':
      return { from: addDays(subMonths(today, 12), 1), to: today };
    case 'all':
      // Falls back to 12 months if the warehouse has no data at all, so the
      // request is still bounded.
      return { from: earliest ?? addDays(subMonths(today, 12), 1), to: today };
  }
}

/** Which preset, if any, exactly matches the active range. */
export function matchPreset(range: DateRange, ctx: ResolveContext): PresetKey {
  for (const p of PRESETS) {
    const r = resolvePreset(p.key, ctx);
    if (r.from === range.from && r.to === range.to) return p.key;
  }
  return 'custom';
}

/**
 * Shift a range backwards/forwards by its own length.
 *
 * A single day steps day-by-day; a 7-day window jumps a week at a time. This is
 * what makes "compare with the previous period" a single click.
 */
export function shiftRange(range: DateRange, direction: -1 | 1): DateRange {
  const span = daysInRange(range);
  return {
    from: addDays(range.from, direction * span),
    to: addDays(range.to, direction * span),
  };
}

export function daysInRange(range: DateRange): number {
  const a = Date.parse(`${range.from}T00:00:00Z`);
  const b = Date.parse(`${range.to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000) + 1;
}

/** Human label for the active range, e.g. "2 Mar 2024" or "1 Sep – 2 Mar 2024". */
export function rangeLabel(range: DateRange, ctx: ResolveContext): string {
  const preset = matchPreset(range, ctx);
  if (preset !== 'custom') {
    return PRESETS.find((p) => p.key === preset)?.label ?? 'Custom';
  }
  if (range.from === range.to) return shortDate(range.from);
  return `${shortDate(range.from)} – ${shortDate(range.to)}`;
}

/** Sub-label showing the concrete dates behind a preset. */
export function rangeSubLabel(range: DateRange): string {
  if (range.from === range.to) return shortDate(range.from);
  const days = daysInRange(range);
  return `${shortDate(range.from)} – ${shortDate(range.to)} · ${days.toLocaleString('en-IN')} days`;
}
